import React, { useEffect, useMemo, useState } from "react";
import { Tab, TabList, makeStyles } from "@fluentui/react-components";
import PropTypes from "prop-types";
import FluentUiIcon from "../../assets/fluent-ui-icon";

const useStyles = makeStyles({
  root: {
    display: "flex",
    "& > button": { textAlign: "justify" },
  },
  nestedRow: {
    padding: "4px 8px",
    paddingLeft: "24px",
    textAlign: "left",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    color: "#424242",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%",
  },
  sectionLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  toggleBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
  },
});

export const FluentTabList = ({
  tabList,
  tabClick,
  currentTab,
  vertical = true,
  nestedItems,
  onNestedClick,
}) => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState(null);

  const sectionsWithNested = useMemo(
    () => new Set(Object.keys(nestedItems || {})),
    [nestedItems]
  );
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    setSelectedTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    if (!sectionsWithNested.size) return;
    setCollapsed((prev) => {
      const next = { ...prev };
      sectionsWithNested.forEach((sid) => {
        if (typeof next[sid] === "undefined") next[sid] = false;
      });
      return next;
    });
  }, [sectionsWithNested]);

  const toggleSection = (sectionId) => {
    setCollapsed((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  return (
    <TabList selectedValue={selectedTab} vertical={vertical} className={styles.root}>
      {tabList?.map((tab) => {
        const subsections = nestedItems?.[tab.Id] || [];
        const hasNested = subsections.length > 0;
        const isCollapsed = hasNested ? !!collapsed[tab.Id] : false;
        const isActive = tab.Id === selectedTab;

        return (
          <React.Fragment key={tab.Id}>
            <Tab
              icon={tab?.Icon}
              value={tab.Id}
              onClick={() => tabClick(tab.Id)}
            >
              <div className={styles.sectionHeader}>
                <span
                  className={styles.sectionLabel}
                  style={{
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#242424" : "#424242",
                  }}
                >
                  {tab.Label}
                </span>

                {hasNested && (
                  <button
                    type="button"
                    aria-label={isCollapsed ? "Expand sub-sections" : "Collapse sub-sections"}
                    className={styles.toggleBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection(tab.Id);
                    }}
                    title={isCollapsed ? "Expand" : "Collapse"}
                  >
                    <FluentUiIcon
                      name={isCollapsed ? "ChevronDownRegular" : "ChevronUpRegular"}
                      color={"#424242"}
                      size={16}
                    />
                  </button>
                )}
              </div>
            </Tab>

            {hasNested && !isCollapsed && (
              <div role="group" style={{ display: "flex", flexDirection: "column" }}>
                {subsections.map((ss) => (
                  <button
                    key={ss.Id}
                    type="button"
                    className={styles.nestedRow}
                    onClick={() => onNestedClick?.(ss.Id, tab.Id)}
                    title={ss.Label}
                  >
                    {ss.Label}
                  </button>
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </TabList>
  );
};

FluentTabList.propTypes = {
  tabList: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.string.isRequired,
      Label: PropTypes.string.isRequired,
      Icon: PropTypes.object,
    })
  ).isRequired,
  tabClick: PropTypes.func.isRequired,
  currentTab: PropTypes.string.isRequired,
  vertical: PropTypes.bool,
  nestedItems: PropTypes.object,
  onNestedClick: PropTypes.func,
};